import { useState, useMemo, useCallback, useEffect, useLayoutEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from '@/utils/toast';
import { useVideoProcessingPoll } from '@/hooks/useVideoProcessingPoll';
import { mediaService } from '@/services/media.service';
import {
  DndContext,
  closestCenter,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { InteractiveSafeKeyboardSensor, InteractiveSafePointerSensor } from '@/utils/dndSensors';
import { MediaUploadZone } from '@/components/composer/MediaUploadZone';
import { MediaItem } from '@/types/posts';
import { Icon } from '@/components/ui/Icon';
import { ICONS } from '@/constants/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faLinkedin,
  faFacebook,
  faInstagram,
  faXTwitter,
  faYoutube,
  faTiktok,
  faPinterest,
  faThreads,
  faGoogle,
  faBluesky,
  faGoogleDrive,
} from '@fortawesome/free-brands-svg-icons';
import { faGlobe } from '@fortawesome/free-solid-svg-icons';
import {
  useGDriveChannels,
  useGDriveImport,
  useConnectGDrive,
  useGDriveFolders,
  friendlyGDriveImportErrorMessage,
} from '@/hooks/useGDriveImport';
import { useGDrivePicker } from '@/hooks/useGDrivePicker';
import { GDriveFolderPickerPanel } from '@/components/gdrive/GDriveFolderPickerPanel';
import { useLockedEntry } from '@/hooks/useLockedEntry';
import { focusSurface } from '@/components/ui/focusStyles';
import { LockBadge } from '@/components/gating/LockBadge';
import type { GDrivePickerFile } from '@/types/gdrive';

const PLATFORM_FA_ICONS: Record<string, IconDefinition> = {
  linkedin: faLinkedin,
  facebook: faFacebook,
  instagram: faInstagram,
  twitter: faXTwitter,
  youtube: faYoutube,
  tiktok: faTiktok,
  pinterest: faPinterest,
  threads: faThreads,
  google_my_business: faGoogle,
  bluesky: faBluesky,
};
import { cn } from '@/utils/cn';
import { Input } from '@/components/ui/Input';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { VideoPlayerModal } from '@/components/ui/VideoPlayerModal';
import { getAccessToken, formatApiError } from '@/services/api';
import { devLog } from '@/utils/logger';
import {
  checkLimits,
  getMostRestrictiveLimit,
  calculatePercentage,
  getPercentageColorClass,
  getProgressBarColorClass,
  getPlatformDisplayName,
} from '@/constants/platformLimits';
import { MediaPicker } from '@/components/media/MediaPicker';
import { MediaLibraryItem } from '@/types/media-library';
import { Tooltip } from '@/components/ui/Tooltip';
import { GDriveMediaBadge } from '@/components/gdrive/GDriveMediaBadge';
import { recordSlowOp } from '@/utils/perfTelemetry';

/**
 * Format file size in human-readable format
 */
function formatFileSize(bytes: number | undefined): string {
  if (!bytes || bytes === 0) return '';

  const units = ['B', 'KB', 'MB', 'GB'];
  let unitIndex = 0;
  let size = bytes;

  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex++;
  }

  return `${size.toFixed(unitIndex > 0 ? 1 : 0)} ${units[unitIndex]}`;
}

// ─────────────────────────────────────────────────────────────────────────────
// VideoProcessingOverlay
// Polls the backend while the async video pipeline is running.
// When READY, fetches the fresh thumbnailUrl and calls onReady so the parent
// can update the MediaItem in-place (no flicker, no broken-image icon).
// ─────────────────────────────────────────────────────────────────────────────

/** Result of the async video pipeline, as delivered to the onReady callback. */
export interface VideoProcessingReadyResult {
  thumbnailUrl?: string;
  /**
   * The video's final playable URL. Populated once the backend has a
   * cloudfrontUrl for this upload. For videos uploaded via the presigned
   * S3 flow (see useMediaUpload.uploadFile), the MediaItem returned at
   * upload time has an empty `url` — the backend doesn't know the final
   * URL until async processing finishes — so this is the only place that
   * URL gets filled in.
   */
  url?: string;
}

interface VideoProcessingOverlayProps {
  /** The upload ID of the video being processed. */
  uploadId: string;
  /** Called once processing reaches READY status with the fresh thumbnail/URL.
   *  Both fields are undefined when the backend reports FAILED. */
  onReady: (result: VideoProcessingReadyResult) => void;
}

function VideoProcessingOverlay({ uploadId, onReady }: VideoProcessingOverlayProps) {
  const { t } = useTranslation(['posts']);
  const [notified, setNotified] = useState(false);

  const { data } = useVideoProcessingPoll(uploadId, !notified);

  // Fire callback exactly once when the terminal state arrives.
  // useEffect avoids setState-during-render React violations.
  useEffect(() => {
    if (notified || !data) return;

    if (data.status === 'READY') {
      setNotified(true);
      // Fetch the full upload record to get the fresh thumbnailUrl (and,
      // for presigned-flow videos, the final playable URL — see
      // VideoProcessingReadyResult).
      mediaService
        .getUploadStatus(uploadId)
        .then((upload) => {
          onReady({
            thumbnailUrl: upload.thumbnailUrl ?? undefined,
            url: upload.cloudFrontUrl ?? undefined,
          });
        })
        .catch(() => {
          onReady({});
        });
    } else if (data.status === 'FAILED') {
      setNotified(true);
      onReady({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.status, notified]);

  // Always render the overlay while this component is mounted (isProcessing=true)
  return (
    <div
      className="absolute inset-0 bg-black/60 z-10 flex flex-col items-center justify-center"
      aria-label={t('processing_video', { defaultValue: 'Processing video…' })}
    >
      <Icon name={ICONS.SPINNER} size="lg" color="text-white" className="animate-spin mb-1.5" />
      <span className="text-white text-xs font-medium">
        {t('processing', { defaultValue: 'Processing…' })}
      </span>
    </div>
  );
}

/**
 * MediaSection Component
 *
 * Media upload with previews and remove buttons.
 * Includes URL fetch option for external images.
 * Shows platform-specific carousel limits when in carousel mode.
 * Clearly indicates whether media will create one carousel or multiple posts.
 */

interface MediaSectionProps {
  media: MediaItem[];
  onAddMedia: (media: MediaItem) => void;
  onRemoveMedia: (mediaId: string) => void;
  onReorderMedia?: (fromIndex: number, toIndex: number) => void;
  onSetMedia?: (media: MediaItem[]) => void;
  onSetChangedThumbnail?: (mediaId: string, thumbnailUrl: string) => void;
  disabled: boolean;
  showCarouselMode?: boolean;
  /** Selected platform keys for limit calculations */
  selectedPlatforms?: string[];
  /**
   * Called when async video processing completes and a thumbnail becomes
   * available (or fails). Callers should update the MediaItem in-place.
   * thumbnailUrl is undefined when processing failed (show placeholder).
   */
  onThumbnailReady?: (mediaId: string, thumbnailUrl: string | undefined, url?: string) => void;
}

/**
 * SortableMediaItem Component
 * Draggable media item for carousel reordering
 */
interface SortableMediaItemProps {
  item: MediaItem;
  index: number;
  onRemove: (mediaId: string) => void;
  disabled: boolean;
  showCarouselMode: boolean;
  isOverLimit: boolean;
  maxCarouselImages: number;
  mediaCount: number;
  onPlayVideo?: (item: MediaItem) => void;
  onChangeCover?: (item: MediaItem) => void;
  onExpand?: (item: MediaItem) => void;
  onThumbnailReady?: (mediaId: string, thumbnailUrl: string | undefined, url?: string) => void;
}

/** Shared video tile controls for sortable and regular media items. */
function VideoTilePreview({
  item,
  onPlayVideo,
  onChangeCover,
  hasTopBadge,
}: {
  item: MediaItem;
  onPlayVideo?: (item: MediaItem) => void;
  onChangeCover?: (item: MediaItem) => void;
  hasTopBadge: boolean;
}) {
  const { t } = useTranslation(['posts', 'common']);

  return (
    <div className="relative flex h-full w-full flex-col items-center justify-center bg-gray-900">
      {item.thumbnail ? (
        <img
          src={item.thumbnail}
          alt={item.filename}
          className="h-full w-full object-contain"
          onError={(event) => { (event.target as HTMLImageElement).style.display = 'none'; }}
        />
      ) : (
        <>
          <Icon name={ICONS.VIDEO} size="lg" color="text-gray-500" />
          <span className="mt-1 text-xs text-gray-500">{t('video')}</span>
        </>
      )}
      {onPlayVideo && (
        <button
          type="button"
          onClick={(event) => { event.stopPropagation(); onPlayVideo(item); }}
          data-dnd-allow-pointer="true"
          aria-label={t('common:play_video')}
          className="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100 [@media(hover:none)]:bg-transparent [@media(hover:none)]:opacity-100"
        >
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-white/90 shadow-lg">
            <span className="ml-1 h-0 w-0 border-y-[8px] border-y-transparent border-l-[14px] border-l-gray-800" />
          </span>
        </button>
      )}
      {onChangeCover && (
        <Tooltip content={t('change_cover')}>
          <button
            type="button"
            onPointerDown={(event) => event.stopPropagation()}
            onClick={(event) => { event.stopPropagation(); onChangeCover(item); }}
            aria-label={t('change_cover')}
            className={cn(
              'absolute left-2 z-20 flex h-8 w-8 items-center justify-center rounded-lg bg-black/70 text-white opacity-0 transition-opacity hover:bg-black/90 group-hover:opacity-100 group-focus-within:opacity-100 focus-visible:opacity-100',
              '[@media(hover:none)]:opacity-100 [@media(hover:none)]:before:absolute [@media(hover:none)]:before:-inset-[6px] [@media(hover:none)]:before:content-[\'\']',
              hasTopBadge ? 'top-[48px]' : 'top-2'
            )}
          >
            <Icon name={ICONS.IMAGE} size="xs" ariaHidden />
          </button>
        </Tooltip>
      )}
    </div>
  );
}

function SortableMediaItem({
  item,
  index,
  onRemove,
  disabled,
  showCarouselMode,
  isOverLimit,
  maxCarouselImages,
  mediaCount,
  onPlayVideo,
  onChangeCover,
  onExpand,
  onThumbnailReady,
}: SortableMediaItemProps) {
  const { t } = useTranslation(['posts', 'common']);
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : undefined,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'relative group aspect-square rounded-lg overflow-hidden bg-gray-100',
        showCarouselMode && index >= maxCarouselImages && 'ring-2 ring-red-500',
        isDragging && 'opacity-50 shadow-2xl ring-2 ring-primary-500',
        showCarouselMode && mediaCount > 1 && 'cursor-grab active:cursor-grabbing'
      )}
      {...attributes}
      {...(showCarouselMode && mediaCount > 1 ? listeners : {})}
    >
      {/* Order badge - visible only in carousel mode with multiple images */}
      {showCarouselMode && mediaCount > 1 && (
        <div
          className={cn(
            'absolute top-2 left-2 w-8 h-8 rounded-full flex items-center justify-center z-20',
            'bg-black/60 hover:bg-black/80 transition-colors',
            index >= maxCarouselImages ? 'bg-red-500 hover:bg-red-600' : ''
          )}
        >
          <span className="text-white text-xs font-bold">{index + 1}</span>
        </div>
      )}

      {/* Multi-post mode: Post number badge (rectangular, shows "Post X") */}
      {!showCarouselMode && mediaCount > 1 && (
        <Tooltip content={t('this_separate_post')}>
          <div
            className="absolute top-2 left-2 px-2 py-1 rounded bg-amber-500 flex items-center justify-center z-10"
          >
            <span className="text-white text-xs font-bold">{t('post_number', { number: index + 1 })}</span>
          </div>
        </Tooltip>
      )}

      {/* Over limit indicator (carousel mode only) */}
      {isOverLimit && (
        <div className="absolute inset-0 bg-red-500/20 z-5 flex items-center justify-center">
          <div className="bg-red-500 text-white text-xs px-2 py-1 rounded font-medium">
            {t('over_limit')}
          </div>
        </div>
      )}

      {/* Upload Progress Overlay */}
      {item.uploadProgress !== undefined && item.uploadProgress < 100 && (
        <div className="absolute inset-0 bg-black/50 z-10 flex flex-col items-center justify-center">
          <Icon name={ICONS.SPINNER} size="lg" color="text-white" className="animate-spin mb-2" />
          <span className="text-white text-sm font-medium">{Math.round(item.uploadProgress)}%</span>
          <div className="w-3/4 h-1.5 bg-white/30 rounded-full mt-2 overflow-hidden">
            <div
              className="h-full bg-white rounded-full transition-all duration-300"
              style={{ width: `${item.uploadProgress}%` }}
            />
          </div>
        </div>
      )}

      {/* Video Processing Overlay — shown while async thumbnail generation is in-flight */}
      {item.type === 'video' && item.isProcessing && onThumbnailReady && (
        <VideoProcessingOverlay
          uploadId={item.id}
          onReady={(result) => onThumbnailReady(item.id, result.thumbnailUrl, result.url)}
        />
      )}

      {/* Preview */}
      {item.type === 'image' ? (
        <img
          src={item.thumbnail || item.url}
          alt={item.filename}
          className="w-full h-full object-cover"
          onError={(e) => {
            (e.target as HTMLImageElement).src =
              'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%23ccc"><path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/></svg>';
          }}
        />
      ) : item.type === 'pdf' ? (
        item.thumbnail ? (
          <img
            src={item.thumbnail}
            alt={item.filename}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center bg-soft-coral">
            <Icon name={ICONS.DOCUMENT} size="lg" color="text-bs-dark" />
            <span className="text-xs text-bs-dark mt-1 font-medium">PDF</span>
          </div>
        )
      ) : (
        <VideoTilePreview
          item={item}
          onPlayVideo={onPlayVideo}
          onChangeCover={onChangeCover}
          hasTopBadge={mediaCount > 1}
        />
      )}

      {/* Expand/Preview Button - visible on hover (images and PDFs only) */}
      {(item.type === 'image' || item.type === 'pdf') && onExpand && (
        <Tooltip content={t('common:preview', { defaultValue: 'Preview' })}>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onExpand(item);
            }}
            className="absolute top-2 right-10 [@media(hover:none)]:right-[52px] w-6 h-6 rounded-full bg-black/60 hover:bg-black/80 flex items-center justify-center transition-colors z-20 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 [@media(hover:none)]:before:absolute [@media(hover:none)]:before:-inset-y-[10px] [@media(hover:none)]:before:-left-[10px] [@media(hover:none)]:before:-right-[4px] [@media(hover:none)]:before:content-['']"
            aria-label={t('common:preview', { defaultValue: 'Preview' })}
          >
            <span className="material-symbols-outlined text-white leading-none" style={{ fontSize: '12px' }}>open_in_full</span>
          </button>
        </Tooltip>
      )}

      {/* Google Drive source badge */}
      <GDriveMediaBadge
        media={item}
        className="absolute bottom-1 left-1 z-20"
      />

      {/* Remove Button */}
      <button
        type="button"
        onClick={() => onRemove(item.id)}
        disabled={disabled}
        className={cn(
          'absolute top-2 right-2',
          'w-6 h-6 rounded-full',
          // 44px touch hit area without changing the 24px visual
          "[@media(hover:none)]:before:absolute [@media(hover:none)]:before:-inset-y-[10px] [@media(hover:none)]:before:-right-[10px] [@media(hover:none)]:before:-left-[4px] [@media(hover:none)]:before:content-['']",
          'bg-red-500 hover:bg-red-600',
          'flex items-center justify-center',
          'transition-colors duration-200',
          'disabled:opacity-50 disabled:cursor-not-allowed',
          'z-20 shadow-md'
        )}
        aria-label={`${t('remove')} ${item.filename || `${t(item.type === 'video' ? 'video' : item.type === 'pdf' ? 'pdf' : 'image')} ${index + 1}`}`}
      >
        <span className="text-white text-sm font-bold leading-none">×</span>
      </button>

      {/* File Info with Size — visible on hover */}
      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-2 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 [@media(hover:none)]:hidden transition-opacity">
        <p className="text-xs text-white truncate">{item.filename || t('untitled')}</p>
        {item.size && item.size > 0 && (
          <p className="text-xs text-white/70">{formatFileSize(item.size)}</p>
        )}
      </div>
    </div>
  );
}

/**
 * Regular (non-draggable) MediaItem Component
 * Used in multi-post mode (no drag-and-drop)
 */
interface RegularMediaItemProps {
  item: MediaItem;
  index: number;
  onRemove: (mediaId: string) => void;
  disabled: boolean;
  mediaCount: number;
  onPlayVideo?: (item: MediaItem) => void;
  onChangeCover?: (item: MediaItem) => void;
  onExpand?: (item: MediaItem) => void;
  onThumbnailReady?: (mediaId: string, thumbnailUrl: string | undefined, url?: string) => void;
}

function RegularMediaItem({
  item,
  index,
  onRemove,
  disabled,
  mediaCount,
  onPlayVideo,
  onChangeCover,
  onExpand,
  onThumbnailReady,
}: RegularMediaItemProps) {
  const { t } = useTranslation(['posts', 'common']);
  return (
    <div
      className={cn(
        'relative group aspect-square rounded-lg overflow-hidden bg-gray-100'
      )}
    >
      {/* Multi-post mode: Post number badge (rectangular, shows "Post X") */}
      {mediaCount > 1 && (
        <Tooltip content={t('this_separate_post')}>
          <div
            className="absolute top-2 left-2 px-2 py-1 rounded bg-amber-500 flex items-center justify-center z-10"
          >
            <span className="text-white text-xs font-bold">{t('post_number', { number: index + 1 })}</span>
          </div>
        </Tooltip>
      )}

      {/* Upload Progress Overlay */}
      {item.uploadProgress !== undefined && item.uploadProgress < 100 && (
        <div className="absolute inset-0 bg-black/50 z-10 flex flex-col items-center justify-center">
          <Icon name={ICONS.SPINNER} size="lg" color="text-white" className="animate-spin mb-2" />
          <span className="text-white text-sm font-medium">{Math.round(item.uploadProgress)}%</span>
          <div className="w-3/4 h-1.5 bg-white/30 rounded-full mt-2 overflow-hidden">
            <div
              className="h-full bg-white rounded-full transition-all duration-300"
              style={{ width: `${item.uploadProgress}%` }}
            />
          </div>
        </div>
      )}

      {/* Video Processing Overlay — shown while async thumbnail generation is in-flight */}
      {item.type === 'video' && item.isProcessing && onThumbnailReady && (
        <VideoProcessingOverlay
          uploadId={item.id}
          onReady={(result) => onThumbnailReady(item.id, result.thumbnailUrl, result.url)}
        />
      )}

      {/* Preview */}
      {item.type === 'image' ? (
        <img
          src={item.thumbnail || item.url}
          alt={item.filename}
          className="w-full h-full object-cover"
          onError={(e) => {
            (e.target as HTMLImageElement).src =
              'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%23ccc"><path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/></svg>';
          }}
        />
      ) : item.type === 'pdf' ? (
        item.thumbnail ? (
          <img
            src={item.thumbnail}
            alt={item.filename}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center bg-soft-coral">
            <Icon name={ICONS.DOCUMENT} size="lg" color="text-bs-dark" />
            <span className="text-xs text-bs-dark mt-1 font-medium">PDF</span>
          </div>
        )
      ) : (
        <VideoTilePreview
          item={item}
          onPlayVideo={onPlayVideo}
          onChangeCover={onChangeCover}
          hasTopBadge={mediaCount > 1}
        />
      )}

      {/* Expand/Preview Button - visible on hover (images and PDFs only) */}
      {(item.type === 'image' || item.type === 'pdf') && onExpand && (
        <Tooltip content={t('common:preview', { defaultValue: 'Preview' })}>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onExpand(item);
            }}
            className="absolute top-2 right-10 [@media(hover:none)]:right-[52px] w-6 h-6 rounded-full bg-black/60 hover:bg-black/80 flex items-center justify-center transition-colors z-20 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 [@media(hover:none)]:before:absolute [@media(hover:none)]:before:-inset-y-[10px] [@media(hover:none)]:before:-left-[10px] [@media(hover:none)]:before:-right-[4px] [@media(hover:none)]:before:content-['']"
            aria-label={t('common:preview', { defaultValue: 'Preview' })}
          >
            <span className="material-symbols-outlined text-white leading-none" style={{ fontSize: '12px' }}>open_in_full</span>
          </button>
        </Tooltip>
      )}

      {/* Google Drive source badge */}
      <GDriveMediaBadge
        media={item}
        className="absolute bottom-1 left-1 z-20"
      />

      {/* Remove Button */}
      <button
        type="button"
        onClick={() => onRemove(item.id)}
        disabled={disabled}
        className={cn(
          'absolute top-2 right-2',
          'w-6 h-6 rounded-full',
          // 44px touch hit area without changing the 24px visual
          "[@media(hover:none)]:before:absolute [@media(hover:none)]:before:-inset-y-[10px] [@media(hover:none)]:before:-right-[10px] [@media(hover:none)]:before:-left-[4px] [@media(hover:none)]:before:content-['']",
          'bg-red-500 hover:bg-red-600',
          'flex items-center justify-center',
          'transition-colors duration-200',
          'disabled:opacity-50 disabled:cursor-not-allowed',
          'z-20 shadow-md'
        )}
        aria-label={`${t('remove')} ${item.filename || `${t(item.type === 'video' ? 'video' : item.type === 'pdf' ? 'pdf' : 'image')} ${index + 1}`}`}
      >
        <span className="text-white text-sm font-bold leading-none">×</span>
      </button>

      {/* File Info with Size — visible on hover */}
      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-2 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 [@media(hover:none)]:hidden transition-opacity">
        <p className="text-xs text-white truncate">{item.filename || t('untitled')}</p>
        {item.size && item.size > 0 && (
          <p className="text-xs text-white/70">{formatFileSize(item.size)}</p>
        )}
      </div>
    </div>
  );
}

/**
 * Post Creation Indicator Component
 * Shows user exactly what will happen when they submit
 */
function PostCreationIndicator({
  mediaCount,
  imageCount,
  isCarouselMode,
  selectedPlatforms,
}: {
  mediaCount: number;
  imageCount: number;
  isCarouselMode: boolean;
  selectedPlatforms: string[];
}) {
  const { t } = useTranslation(['posts', 'common']);

  const limitCheck = useMemo(() => {
    if (!isCarouselMode) return null;
    return checkLimits(imageCount, selectedPlatforms, 'carouselImages');
  }, [imageCount, selectedPlatforms, isCarouselMode]);

  // No indicator needed for no media
  if (mediaCount === 0) {
    return null;
  }

  // Carousel mode — combined indicator
  if (isCarouselMode) {
    if (imageCount === 1) {
      return (
        <div className="mt-3 flex items-start gap-2 p-3 bg-soft-mint border border-light-teal-gray rounded-lg">
          <Icon name={ICONS.INFO} size="sm" color="text-dark-teal" className="flex-shrink-0 mt-0.5" />
          <p className="text-sm text-dark-teal">
            {t('add_more_images_carousel')}
          </p>
        </div>
      );
    }

    const { mostRestrictiveLimit, overLimits, withinLimits, isWithinAll } = limitCheck!;
    const percentage = calculatePercentage(imageCount, mostRestrictiveLimit);
    const colorClass = getPercentageColorClass(percentage);
    const progressColorClass = getProgressBarColorClass(percentage);

    return (
      <div className="mt-3 p-3 bg-soft-mint border border-light-teal-gray rounded-lg space-y-2">
        {/* Header: carousel count + status */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            <Icon name={ICONS.GALLERY} size="xs" color="text-dark-teal" />
            <span className="text-xs text-dark-teal">
              {t('create_carousel_post', { count: imageCount })}
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className={cn('text-xs font-medium', colorClass)}>
              {imageCount}/{mostRestrictiveLimit}
            </span>
            {isWithinAll ? (
              <Icon name={ICONS.TICK_CIRCLE} size="xs" color="text-success" />
            ) : (
              <Icon name={ICONS.CAUTION} size="xs" color="text-danger" />
            )}
          </div>
        </div>

        {/* Progress bar */}
        <div className="h-1 w-full bg-white/60 rounded-full overflow-hidden">
          <div
            className={cn('h-full transition-all duration-200 rounded-full', progressColorClass)}
            style={{ width: `${Math.min(percentage, 100)}%` }}
          />
        </div>

        {/* Platform breakdown (icons with tooltips) */}
        {(overLimits.length > 0 || withinLimits.length > 0) && (
          <div className="flex items-center gap-1.5">
            {withinLimits.map(({ platform, limit, displayName }) => (
              <Tooltip key={platform} content={`${displayName} (${limit})`}>
                <span className="w-6 h-6 inline-flex items-center justify-center rounded-full bg-white/60 text-dark-teal">
                  <FontAwesomeIcon icon={PLATFORM_FA_ICONS[platform] || faGlobe} className="text-[13px]" />
                </span>
              </Tooltip>
            ))}
            {overLimits.map(({ platform, limit, displayName, overBy }) => (
              <Tooltip key={platform} content={`${displayName} (${limit}) — ${t('over_by', { count: overBy, limit })}`}>
                <span className="w-6 h-6 inline-flex items-center justify-center rounded-full bg-red-100 text-danger">
                  <FontAwesomeIcon icon={PLATFORM_FA_ICONS[platform] || faGlobe} className="text-[13px]" />
                </span>
              </Tooltip>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Multi-post mode indicators (carousel OFF)
  if (mediaCount === 1) {
    return null;
  }

  return (
    <div className="mt-3 flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
      <Icon name={ICONS.COPY} size="sm" color="text-amber-600" className="flex-shrink-0 mt-0.5" />
      <div className="flex-1">
        <p className="text-sm font-medium text-amber-700">
          {t('create_separate_posts', { count: mediaCount })}
        </p>
        <p className="text-xs text-amber-600 mt-1">
          {t('enable_carousel_tip')}
        </p>
      </div>
    </div>
  );
}

/**
 * Carousel Limits Display Component
 * Shows current carousel count vs platform limits
 */
function CarouselLimitsDisplay({
  imageCount,
  platforms,
}: {
  imageCount: number;
  platforms: string[];
}) {
  const { t } = useTranslation(['posts', 'common']);
  const limitCheck = useMemo(() => {
    return checkLimits(imageCount, platforms, 'carouselImages');
  }, [imageCount, platforms]);

  const {
    mostRestrictiveLimit,
    mostRestrictivePlatform,
    overLimits,
    withinLimits,
    isWithinAll,
  } = limitCheck;

  const percentage = calculatePercentage(imageCount, mostRestrictiveLimit);
  const colorClass = getPercentageColorClass(percentage);
  const progressColorClass = getProgressBarColorClass(percentage);

  // Don't show if no images
  if (imageCount === 0) {
    return null;
  }

  return (
    <div className="mt-3 p-3 bg-gray-50 border border-gray-200 rounded-lg space-y-2">
      {/* Header with count */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon name={ICONS.GALLERY} size="sm" color="text-bs-gray-stone" />
          <span className="text-sm font-medium text-bs-dark">
            {t('carousel_images')}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className={`text-sm font-medium ${colorClass}`}>
            {imageCount} / {mostRestrictiveLimit}
          </span>
          {!isWithinAll && (
            <Icon name={ICONS.CAUTION} size="sm" color="text-danger" />
          )}
          {isWithinAll && (
            <Icon name={ICONS.TICK_CIRCLE} size="sm" color="text-success" />
          )}
        </div>
      </div>

      {/* Progress bar */}
      <div className="h-1.5 w-full bg-gray-200 rounded-full overflow-hidden">
        <div
          className={`h-full transition-all duration-200 ${progressColorClass}`}
          style={{ width: `${Math.min(percentage, 100)}%` }}
        />
      </div>

      {/* Platform info */}
      {platforms.length > 1 && (
        <p className="text-xs text-bs-gray">
          {t('based_on_limit', { platform: getPlatformDisplayName(mostRestrictivePlatform) })}
        </p>
      )}

      {/* Platform breakdown */}
      {(overLimits.length > 0 || withinLimits.length > 0) && (
        <div className="pt-2 border-t border-gray-200 space-y-1">
          {/* Over limit warnings */}
          {overLimits.map(({ platform, limit, displayName, overBy }) => (
            <div
              key={platform}
              className="flex items-center gap-2 text-xs text-danger"
            >
              <Icon name={ICONS.CAUTION} size="xs" color="text-danger" />
              <span>
                <span className="font-medium">{displayName}</span>: {t('over_by', { count: overBy, limit })}
              </span>
            </div>
          ))}

          {/* Within limit */}
          {withinLimits.length > 0 && (
            <div className="flex items-center gap-2 text-xs text-success">
              <Icon name={ICONS.TICK_CIRCLE} size="xs" color="text-success" />
              <span>
                {t('ok_for', { platforms: withinLimits.map(({ displayName, limit }) => `${displayName} (${limit})`).join(', ') })}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export const MediaSection = ({
  media,
  onAddMedia,
  onRemoveMedia,
  onReorderMedia,
  onSetMedia,
  onSetChangedThumbnail,
  disabled,
  showCarouselMode = false,
  selectedPlatforms = ['linkedin'],
  onThumbnailReady: onThumbnailReadyProp,
}: MediaSectionProps) => {
  const __renderStart = performance.now();
  useLayoutEffect(() => {
    recordSlowOp('render:MediaSection', performance.now() - __renderStart);
  });
  const { t } = useTranslation(['posts', 'common']);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [externalUrl, setExternalUrl] = useState('');
  const [isFetchingUrl, setIsFetchingUrl] = useState(false);

  // State for video removal confirmation
  const [videoToRemove, setVideoToRemove] = useState<MediaItem | null>(null);
  const [showVideoConfirm, setShowVideoConfirm] = useState(false);

  // State for media library picker
  const [showMediaLibrary, setShowMediaLibrary] = useState(false);
  const [showUrlFetch, setShowUrlFetch] = useState(false);

  // Video playback state
  const [videoToPlay, setVideoToPlay] = useState<MediaItem | null>(null);

  // Click handler for video thumbnails.
  // GDrive deferred videos (no S3 URL yet) are previewed via Drive's iframe
  // when a driveFileId is available. Without driveFileId we fall back to a
  // toast (defensive: shouldn't happen in practice).
  const handlePlayVideo = useCallback((item: MediaItem) => {
    if (!item.url || item.url === '') {
      if (item.sourceType === 'GDRIVE') {
        if (item.driveFileId) {
          // Open the modal — VideoPlayerModal will render an iframe
          setVideoToPlay(item);
          return;
        }
        // GDrive item but no driveFileId — shouldn't happen, surface as info
        toast.info(t('gdrive_video_preview_unavailable', {
          defaultValue: 'Video will be fetched from Google Drive when the post publishes. Preview not available yet.',
        }));
        return;
      }
      // Non-GDrive empty URL is a data bug; still surface via toast
      toast.error(t('video_preview_unavailable', {
        defaultValue: 'Video preview not available.',
      }));
      return;
    }
    setVideoToPlay(item);
  }, [t]);

  // Lightbox state for image/PDF preview
  const [lightboxItem, setLightboxItem] = useState<MediaItem | null>(null);

  // Video cover change state
  const [videoForCoverChange, setVideoForCoverChange] = useState<MediaItem | null>(null);
  const [isUploadingCover, setIsUploadingCover] = useState(false);

  // ── Google Drive picker wiring ───────────────────────────────────────────────
  // Entitlement (not the raw package column) so brand-level plan profiles reach Drive in the UI too
  // Drive is plan-gated: the button stays visible but locked (click opens the upsell) when the plan lacks it
  const gdriveLock = useLockedEntry('gdrive');
  const gdriveEnabled = gdriveLock.decided && !gdriveLock.locked;
  const { data: gdriveChannels } = useGDriveChannels({ enabled: gdriveEnabled });
  // Strict: only an actually-active channel counts. Falling back to a
  // disconnected channel was a bug — the OAuth-redirect branch in
  // handleGDriveClick would never fire after the user clicked Disconnect.
  const activeGDriveChannel = gdriveChannels?.find(c => c.isActive);
  const gdriveImportMutation = useGDriveImport();
  const gdriveConnectMutation = useConnectGDrive();

  // Access mode (docs/features/gdrive-folder-scoping/ACCESS-MODE.md extension
  // of SPEC.md §5.2). Always branch on `mode`, never on `folders.length` —
  // `mode: 'all'` also returns an empty `folders` array. `'unconfigured'`
  // blocks the browse Picker client-side (server also fails closed).
  const { data: gdriveFoldersData, isLoading: isLoadingGDriveFolders } = useGDriveFolders(
    activeGDriveChannel?.id ?? null
  );
  const gdriveSharedFolders = gdriveFoldersData?.folders ?? [];
  const gdriveMode = gdriveFoldersData?.mode ?? 'unconfigured';
  const gdriveCanEditFolders = gdriveFoldersData?.canEdit ?? false;
  const [showGDriveFolderPrompt, setShowGDriveFolderPrompt] = useState(false);

  const handleGDrivePickerSelect = useCallback(
    async (files: GDrivePickerFile[]) => {
      if (!activeGDriveChannel || files.length === 0) return;
      try {
        const result = await gdriveImportMutation.mutateAsync({
          fileIds: files.map(f => f.id),
        });

        const errCount = result.errors?.length ?? 0;
        if (errCount > 0 && result.created.length > 0) {
          toast.warning(t('common:gdrive_import_partial', { ok: result.created.length, fail: errCount }));
        } else if (errCount > 0 && result.created.length === 0) {
          // Single-error case (or multi-error where every failure shares the
          // same code, e.g. all rejected as GDRIVE_FOLDER_NOT_ALLOWED) gets
          // the specific, friendlier message instead of a generic count.
          const firstError = result.errors[0];
          const allSameCode = result.errors.every((e) => e.code === firstError.code);
          const message =
            result.errors.length === 1 || allSameCode
              ? friendlyGDriveImportErrorMessage(firstError.code, firstError.message)
              : t('common:gdrive_import_failed');
          toast.error(message);
        } else {
          toast.success(t('common:gdrive_import_success', { count: result.created.length }));
        }

        for (const created of result.created) {
          // Map GDriveCreatedMedia → MediaItem.
          // originalUrl is null for deferred rows; url is required by MediaItem
          // so we use '' as a placeholder. The thumbnail is tried first in the
          // image renderer, and the onError handler shows a placeholder icon.
          // Backend returns 'DOCUMENT' for PDFs (application/pdf). MediaItem uses
          // lowercase 'pdf' for that case, so we normalise explicitly.
          const rawType = created.type.toLowerCase();
          const lowerType: 'image' | 'video' | 'pdf' =
            rawType === 'video' ? 'video' : rawType === 'image' ? 'image' : 'pdf';
          const mediaItem: MediaItem = {
            id: created.id.toString(),
            url: created.originalUrl ?? '',
            thumbnail: created.thumbnailUrl ?? undefined,
            type: lowerType,
            filename: undefined,
            size: created.size ?? 0,
            width: created.width ?? undefined,
            height: created.height ?? undefined,
            sourceType: 'GDRIVE',
            driveFileId: created.sourceRef?.driveFileId,
          };
          onAddMedia(mediaItem);
        }
      } catch (err) {
        // formatApiError surfaces the backend's own message (e.g. a friendly
        // GDRIVE_FOLDER_NOT_ALLOWED / NO_TEAM_CONTEXT string) instead of
        // Axios's generic "Request failed with status code 4xx".
        const { message } = formatApiError(err);
        toast.error(message || t('common:gdrive_import_failed'));
      }
    },
    [activeGDriveChannel, gdriveImportMutation, onAddMedia, t]
  );

  const { open: openGDrivePicker } = useGDrivePicker({
    channelId: activeGDriveChannel?.id ?? 0,
    onSelect: handleGDrivePickerSelect,
    allowedFolders: gdriveSharedFolders,
    // mode:'all' (ACCESS-MODE extension) — whole Drive, no folder lock.
    unrestricted: gdriveMode === 'all',
  });

  const handleGDriveClick = useCallback(() => {
    // Move focus off the trigger button before opening Picker. Radix Dialog
    // sets aria-hidden on the composer overlay when Picker becomes the topmost
    // dialog; a focused descendant of an aria-hidden ancestor is an a11y
    // violation. Blurring before the open avoids the warning entirely.
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    if (!activeGDriveChannel) {
      // Not connected — kick off OAuth. useConnectGDrive already redirects on
      // success via its onSuccess handler (window.location.href = authUrl).
      gdriveConnectMutation.mutate(undefined);
      return;
    }
    if (isLoadingGDriveFolders) return; // avoid a flash between "connected" and folders resolving
    if (gdriveMode === 'unconfigured') {
      // UNCONFIGURED channel (SPEC §5.1/§5.2) — imports fail closed server-side
      // regardless, so don't even open the browse Picker.
      if (gdriveCanEditFolders) {
        setShowGDriveFolderPrompt(true);
      } else {
        toast.info(
          t('common:gdrive_waiting_for_folders', {
            defaultValue: 'Waiting for the teammate who connected Google Drive to choose shared folders.',
          })
        );
      }
      return;
    }
    void openGDrivePicker();
  }, [
    activeGDriveChannel,
    gdriveConnectMutation,
    openGDrivePicker,
    isLoadingGDriveFolders,
    gdriveMode,
    gdriveCanEditFolders,
    t,
  ]);

  /**
   * Handle video cover image upload
   * Uses direct upload (backend handles S3) to avoid CORS issues
   * Defers database save until post save
   */
  const handleCoverUpload = useCallback(
    async (file: File) => {
      if (!videoForCoverChange) return;

      setIsUploadingCover(true);

      try {
        // Use direct upload - backend handles S3, no CORS issues
        const formData = new FormData();
        formData.append('file', file);

        const uploadResponse = await fetch('/api/media/direct-upload', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${getAccessToken()}`,
          },
          body: formData,
        });

        const uploadData = await uploadResponse.json();
        if (!uploadData.success) throw new Error(uploadData.error || 'Failed to upload cover image');

        const thumbnailUrl = uploadData.data.upload.optimizedUrl || uploadData.data.upload.originalUrl;

        // Update the local media item in place (preserve order and avoid state issues)
        const updatedItem: MediaItem = {
          ...videoForCoverChange,
          thumbnail: thumbnailUrl,
        };

        // Update media array in place to avoid remove+add state sync issues
        if (onSetMedia) {
          const updatedMedia = media.map((item) =>
            item.id === videoForCoverChange.id ? updatedItem : item
          );
          onSetMedia(updatedMedia);
        } else {
          // Fallback: remove and re-add (may cause issues)
          onRemoveMedia(videoForCoverChange.id);
          onAddMedia(updatedItem);
        }

        // Track thumbnail change for deferred save (will be saved when post is saved)
        if (onSetChangedThumbnail) {
          onSetChangedThumbnail(videoForCoverChange.id, thumbnailUrl);
        }

        setVideoForCoverChange(null);
      } catch (error) {
        devLog.error('Failed to update video cover:', error);
        setUploadError(t('cover_upload_failed'));
      } finally {
        setIsUploadingCover(false);
      }
    },
    [videoForCoverChange, onRemoveMedia, onAddMedia, onSetMedia, onSetChangedThumbnail, media, t]
  );

  /**
   * Handle media removal with confirmation for videos
   * Videos take longer to upload, so we confirm before removing
   */
  const handleRemoveMedia = useCallback(
    (mediaId: string) => {
      const mediaItem = media.find((m) => m.id === mediaId);

      if (mediaItem?.type === 'video') {
        // Show confirmation dialog for videos
        setVideoToRemove(mediaItem);
        setShowVideoConfirm(true);
      } else {
        // Remove images immediately
        onRemoveMedia(mediaId);
      }
    },
    [media, onRemoveMedia]
  );

  /**
   * Confirm video removal
   */
  const handleConfirmVideoRemove = useCallback(() => {
    if (videoToRemove) {
      onRemoveMedia(videoToRemove.id);
      setVideoToRemove(null);
      setShowVideoConfirm(false);
    }
  }, [videoToRemove, onRemoveMedia]);

  /**
   * Cancel video removal
   */
  const handleCancelVideoRemove = useCallback(() => {
    setVideoToRemove(null);
    setShowVideoConfirm(false);
  }, []);

  /**
   * Handle video processing completion — update thumbnail in-place and clear
   * the isProcessing flag so the overlay unmounts.
   */
  const handleThumbnailReady = useCallback(
    (mediaId: string, thumbnailUrl: string | undefined, url?: string) => {
      const updatedMedia = media.map((item) => {
        if (item.id !== mediaId) return item;
        return {
          ...item,
          thumbnail: thumbnailUrl,
          isProcessing: false,
          // Videos uploaded via the presigned S3 flow have an empty `url`
          // until the async pipeline finishes (see useMediaUpload.uploadFile
          // and VideoProcessingReadyResult) — fill it in now if the backend
          // has one. Never overwrite an existing url with an empty string.
          ...(url ? { url } : {}),
        };
      });

      if (onSetMedia) {
        onSetMedia(updatedMedia);
      }

      // Propagate to external caller if provided (e.g. PostCreator saving deferred thumbnail)
      if (onThumbnailReadyProp) {
        onThumbnailReadyProp(mediaId, thumbnailUrl);
      }
    },
    [media, onSetMedia, onThumbnailReadyProp]
  );

  // Configure dnd-kit sensors for pointer and keyboard accessibility
  const sensors = useSensors(
    useSensor(InteractiveSafePointerSensor, {
      activationConstraint: {
        distance: 8, // 8px movement before drag starts
      },
    }),
    useSensor(InteractiveSafeKeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Calculate the most restrictive carousel limit
  const maxCarouselImages = useMemo(() => {
    return getMostRestrictiveLimit(selectedPlatforms, 'carouselImages');
  }, [selectedPlatforms]);

  // Count only images for carousel limit
  const imageCount = useMemo(() => {
    return media.filter((m) => m.type === 'image').length;
  }, [media]);

  // Check if over carousel limit
  const isOverCarouselLimit = showCarouselMode && imageCount > maxCarouselImages;

  /**
   * Handle media upload completion
   */
  const handleUploadComplete = (newMedia: MediaItem[]) => {
    newMedia.forEach((item) => {
      if (!media.find((m) => m.id === item.id)) {
        onAddMedia(item);
      }
    });
    setUploadError(null);
  };

  /**
   * Handle media upload error
   */
  const handleUploadError = (error: Error) => {
    setUploadError(`Upload failed: ${error.message}`);
  };

  /**
   * Fetch image from external URL via backend
   * Backend downloads, uploads to S3, returns real UUID
   */
  const handleFetchFromUrl = async () => {
    if (!externalUrl.trim()) return;

    // Basic URL validation
    try {
      new URL(externalUrl);
    } catch {
      setUploadError('Please enter a valid URL');
      return;
    }

    setIsFetchingUrl(true);
    setUploadError(null);

    try {
      // Call backend to download and upload the image (longer timeout for large images)
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 60000); // 60 second timeout

      const response = await fetch('/api/media/upload-url', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getAccessToken()}`,
        },
        body: JSON.stringify({ url: externalUrl }),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Failed to upload image from URL');
      }

      // Create MediaItem from backend response
      const uploadedMedia = result.data;
      const newMedia: MediaItem = {
        id: uploadedMedia.id.toString(),
        url: uploadedMedia.originalUrl,
        thumbnail: uploadedMedia.thumbnailUrl,
        type: 'image',
        filename: uploadedMedia.filename || 'external-image',
        size: uploadedMedia.fileSize || 0,
        width: uploadedMedia.width,
        height: uploadedMedia.height,
      };

      onAddMedia(newMedia);
      setExternalUrl('');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to add image from URL';
      setUploadError(errorMessage);
      toast.error(errorMessage);
      devLog.error('External URL fetch error:', err);
    } finally {
      setIsFetchingUrl(false);
    }
  };

  // Calculate dynamic max files based on carousel mode
  const effectiveMaxFiles = showCarouselMode
    ? Math.max(maxCarouselImages - media.length, 0)
    : Math.max(10 - media.length, 0);

  /**
   * Handle drag end - reorder media items
   */
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id && onReorderMedia) {
      const oldIndex = media.findIndex((m) => m.id === active.id);
      const newIndex = media.findIndex((m) => m.id === over.id);
      onReorderMedia(oldIndex, newIndex);
    }
  };

  /**
   * Handle media library selection
   * Convert MediaLibraryItem to MediaItem format
   */
  const handleMediaLibrarySelect = useCallback(
    (selectedMedia: MediaLibraryItem[]) => {
      const existingIds = new Set(media.map((m) => m.id));
      selectedMedia.forEach((item) => {
        const itemId = item.id.toString();
        if (existingIds.has(itemId)) return; // Skip already added
        const mediaItem: MediaItem = {
          id: itemId,
          url: item.url,
          thumbnail: item.thumbnailUrl,
          type: item.type.toLowerCase() as 'image' | 'video' | 'pdf',
          filename: item.filename || 'media-file',
          size: item.fileSize || 0,
          width: item.width,
          height: item.height,
          // Pass through mimeType so GIF detection works for library imports
          mimeType: item.mimeType,
        };
        onAddMedia(mediaItem);
        existingIds.add(itemId); // Track within this batch too
      });
      setShowMediaLibrary(false);
    },
    [onAddMedia, media]
  );

  return (
    <div>
      {/* Non-carousel file count hint */}
      {!showCarouselMode && media.length > 0 && (
        <div className="flex items-center justify-end mb-3">
          <span className="text-xs text-bs-gray">{t('max_x_files', { count: 10 })}</span>
        </div>
      )}

      {/* Upload Error */}
      {uploadError && (
        <div
          className="mb-3 flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg"
          role="alert"
        >
          <Icon
            name={ICONS.CAUTION}
            size="sm"
            color="text-red-600"
            className="flex-shrink-0 mt-0.5"
          />
          <p className="text-sm text-red-600">{uploadError}</p>
          <button
            type="button"
            onClick={() => setUploadError(null)}
            className="ml-auto text-red-600 hover:text-red-800 hover:bg-red-100 rounded-full p-1.5 -m-1 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-red-500"
            aria-label={t('dismiss_error')}
          >
            <Icon name={ICONS.CROSS} size="sm" />
          </button>
        </div>
      )}

      {/* Post Creation Indicator - Combined carousel info + limits */}
      <PostCreationIndicator
        mediaCount={media.length}
        imageCount={imageCount}
        isCarouselMode={showCarouselMode}
        selectedPlatforms={selectedPlatforms}
      />

      {/* Media Previews - Carousel Mode with Drag and Drop */}
      {media.length > 0 && showCarouselMode && onReorderMedia && (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={media.map((m) => m.id)}
            strategy={rectSortingStrategy}
          >
            <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-3">
              {media.map((item, index) => (
                <SortableMediaItem
                  key={item.id}
                  item={item}
                  index={index}
                  onRemove={handleRemoveMedia}
                  disabled={disabled}
                  showCarouselMode={showCarouselMode}
                  isOverLimit={showCarouselMode && index >= maxCarouselImages}
                  maxCarouselImages={maxCarouselImages}
                  mediaCount={media.length}
                  onPlayVideo={handlePlayVideo}
                  onChangeCover={setVideoForCoverChange}
                  onExpand={setLightboxItem}
                  onThumbnailReady={handleThumbnailReady}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      {/* Media Previews - Non-Carousel Mode (no drag and drop) */}
      {media.length > 0 && (!showCarouselMode || !onReorderMedia) && (
        <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-3">
          {media.map((item, index) => (
            <RegularMediaItem
              key={item.id}
              item={item}
              index={index}
              onRemove={handleRemoveMedia}
              disabled={disabled}
              mediaCount={media.length}
              onPlayVideo={handlePlayVideo}
              onChangeCover={setVideoForCoverChange}
              onExpand={setLightboxItem}
              onThumbnailReady={handleThumbnailReady}
            />
          ))}
        </div>
      )}

      {/* Drag hint for carousel mode */}
      {showCarouselMode && media.length > 1 && onReorderMedia && (
        <p className="mt-2 text-xs text-bs-gray text-center">
          <span className="inline-flex items-center gap-1">
            <Icon name={ICONS.INFO} size="xs" color="text-gray-400" />
            {t('drag_images_reorder')}
          </span>
        </p>
      )}

      {/* Compact Upload Zone */}
      {effectiveMaxFiles > 0 && !disabled && (
        <div className="mt-3">
          {/* Action buttons */}
          <div className="flex items-center gap-1 mb-1.5">
            <Tooltip content={t('browse_media_library')}>
              <button
                type="button"
                onClick={() => setShowMediaLibrary(true)}
                className="w-7 h-7 inline-flex items-center justify-center rounded-md text-gray-400 hover:text-bs-black hover:bg-gray-100 transition-colors"
              >
                <Icon name={ICONS.GALLERY} size="sm" />
              </button>
            </Tooltip>
            <Tooltip content={t('fetch_external_url')}>
              <button
                type="button"
                onClick={() => setShowUrlFetch(!showUrlFetch)}
                className={cn(
                  'w-7 h-7 inline-flex items-center justify-center rounded-md transition-colors',
                  showUrlFetch ? 'text-bs-black bg-gray-100' : 'text-gray-400 hover:text-bs-black hover:bg-gray-100'
                )}
              >
                <Icon name={ICONS.LINK} size="sm" />
              </button>
            </Tooltip>

            {/* Google Drive import button. Plan without Drive: shown locked, click opens the upsell. */}
            {gdriveLock.decided && gdriveLock.locked && (
              <Tooltip content={gdriveLock.label}>
                <button
                  type="button"
                  onClick={gdriveLock.showUpsell}
                  data-testid={gdriveLock.testId}
                  className={cn("relative w-7 h-7 inline-flex items-center justify-center rounded-md text-gray-400 hover:text-bs-black hover:bg-gray-100 transition-colors", focusSurface)}
                  aria-label={gdriveLock.ariaLabelFor(t('common:import_from_gdrive'))}
                >
                  <FontAwesomeIcon icon={faGoogleDrive} className="w-4 h-4" />
                  <LockBadge size="xs" className="absolute -right-1 -bottom-1" />
                </button>
              </Tooltip>
            )}
            {gdriveEnabled && (
              <Tooltip content={activeGDriveChannel ? t('common:import_from_gdrive') : t('common:gdrive_connect_first_in_settings')}>
                <button
                  type="button"
                  onClick={handleGDriveClick}
                  disabled={gdriveConnectMutation.isPending}
                  className="w-7 h-7 inline-flex items-center justify-center rounded-md text-gray-400 hover:text-bs-black hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  aria-label={activeGDriveChannel ? t('common:import_from_gdrive') : t('common:gdrive_connect_first_in_settings')}
                >
                  <FontAwesomeIcon icon={faGoogleDrive} className="w-4 h-4" />
                </button>
              </Tooltip>
            )}
          </div>

          {/* Folder-selection step (SPEC §5.1) — shown inline when the connected
              GDrive channel is still UNCONFIGURED and the current user can edit
              its scope. Deliberately NOT a modal/overlay — see
              GDriveFolderPickerPanel.tsx's positioning-bug postmortem. */}
          {activeGDriveChannel && showGDriveFolderPrompt && (
            <div className="mt-2">
              <GDriveFolderPickerPanel
                open={showGDriveFolderPrompt}
                onOpenChange={setShowGDriveFolderPrompt}
                channelId={activeGDriveChannel.id}
              />
            </div>
          )}

          <MediaUploadZone
            onUploadComplete={handleUploadComplete}
            onUploadError={handleUploadError}
            maxFiles={effectiveMaxFiles}
            acceptVideo={!showCarouselMode}
            acceptPdf={true}
          />

          {/* Collapsible URL fetch input */}
          {showUrlFetch && (
            <div className="mt-2 flex gap-2">
              <Input
                inputSize="sm"
                type="url"
                placeholder="https://example.com/image.jpg"
                value={externalUrl}
                onChange={(e) => setExternalUrl(e.target.value)}
                disabled={disabled || isFetchingUrl}
                className="flex-1"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleFetchFromUrl();
                  }
                }}
              />
              <button
                type="button"
                onClick={handleFetchFromUrl}
                disabled={disabled || isFetchingUrl || !externalUrl.trim()}
                className={cn(
                  'px-2 lg:px-3 h-[34px] lg:h-auto lg:py-1.5 bg-white text-bs-black text-xs lg:text-sm font-medium rounded-lg',
                  'border border-gray-300 hover:bg-bs-black hover:text-white transition-colors',
                  'disabled:opacity-50 disabled:cursor-not-allowed'
                )}
              >
                {isFetchingUrl ? (
                  <Icon name={ICONS.SPINNER} size="sm" className="animate-spin" />
                ) : (
                  t('fetch')
                )}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Over limit warning */}
      {showCarouselMode && effectiveMaxFiles === 0 && !isOverCarouselLimit && (
        <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-center gap-2">
          <Icon name={ICONS.INFO} size="sm" color="text-yellow-600" />
          <p className="text-sm text-yellow-700">
            {t('max_carousel_reached')}
          </p>
        </div>
      )}

      {/* Video Player Modal */}
      <VideoPlayerModal
        isOpen={!!videoToPlay}
        onClose={() => setVideoToPlay(null)}
        videoUrl={videoToPlay?.url || ''}
        iframeUrl={
          videoToPlay?.sourceType === 'GDRIVE' &&
          (!videoToPlay.url || videoToPlay.url === '') &&
          videoToPlay.driveFileId
            ? `https://drive.google.com/file/d/${videoToPlay.driveFileId}/preview`
            : undefined
        }
        title={videoToPlay?.filename}
      />

      {/* Media Lightbox Modal */}
      {lightboxItem && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80"
          onClick={() => setLightboxItem(null)}
        >
          {/* Close button */}
          <button
            type="button"
            onClick={() => setLightboxItem(null)}
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-black/50 hover:bg-black/70 text-white hover:text-gray-200 flex items-center justify-center transition-all duration-200 z-10 focus:outline-none"
            aria-label={t('common:close')}
          >
            <Icon name={ICONS.CLOSE} size="sm" />
          </button>

          {/* Media content */}
          <div
            className="max-w-[90vw] max-h-[90vh] overflow-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {lightboxItem.type === 'image' ? (
              <img
                src={lightboxItem.url}
                alt={lightboxItem.filename}
                className="max-w-full max-h-[85vh] object-contain rounded-lg"
              />
            ) : lightboxItem.type === 'pdf' ? (
              <iframe
                src={lightboxItem.url}
                className="w-[80vw] max-w-4xl rounded-lg bg-white"
                style={{ height: '85vh' }}
                title={lightboxItem.filename || 'PDF Document'}
              />
            ) : null}
          </div>

          {/* Filename label */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-4 py-2 bg-black/60 rounded-full">
            <span className="text-white text-sm">{lightboxItem.filename}</span>
          </div>
        </div>
      )}

      {/* Video Cover Upload Dialog */}
      <ConfirmDialog
        open={!!videoForCoverChange}
        onOpenChange={(open) => !open && setVideoForCoverChange(null)}
        title={t('change_video_cover')}
        description={t('upload_image_as_cover')}
        variant="default"
        confirmText={t('select_image')}
        cancelText={t('cancel')}
        onConfirm={() => {
          // Trigger file input
          const input = document.createElement('input');
          input.type = 'file';
          input.accept = 'image/*';
          input.onchange = (e) => {
            const file = (e.target as HTMLInputElement).files?.[0];
            if (file) handleCoverUpload(file);
          };
          input.click();
        }}
        onCancel={() => setVideoForCoverChange(null)}
        loading={isUploadingCover}
      />

      {/* Video Removal Confirmation Dialog */}
      <ConfirmDialog
        open={showVideoConfirm}
        onOpenChange={setShowVideoConfirm}
        title={t('remove_video')}
        description={t('video_upload_warning', { filename: videoToRemove?.filename || t('video') })}
        variant="warning"
        confirmText={t('remove')}
        cancelText={t('keep')}
        onConfirm={handleConfirmVideoRemove}
        onCancel={handleCancelVideoRemove}
      />

      {/* Media Library Picker */}
      <MediaPicker
        isOpen={showMediaLibrary}
        onClose={() => setShowMediaLibrary(false)}
        onSelect={handleMediaLibrarySelect}
        mode="multi"
        maxSelection={effectiveMaxFiles}
        allowedTypes={showCarouselMode ? ['IMAGE'] : undefined}
        title={t('select_media_from_library')}
        nested={true}
      />
    </div>
  );
};
